/**
 * Portal placeholders (Option C C3 ruling after review r5): the Azure portal's "Connection strings" blades — and the
 * documentation templates they come from — leave `{your_password}` (JDBC: `{your_password_here}`) where the password
 * goes, and some leave `{your_username}` or `{your_database}`. A value that is ENTIRELY such a placeholder counts as "not
 * given": the field stays empty (the preview says it is still needed) and nothing is refused. A value that merely
 * contains the word is an ordinary value.
 *
 * The pattern is tight, over the whole value AS WRITTEN (an ODBC / JDBC `{…}` value keeps its braces here): `{…}`, `<…>`
 * or `[…]` around `[your ]<word>[ here]`, or a bare `your <word>[ here]` — `_`, `-` or a space between the words, any
 * letter case, spaces inside the marks. Words: password / passwd / pwd; username / user name / user / user id / login;
 * database / database name / db / dbname.
 */
import type { KeyValuePair } from '@/core/utils/connectionStringTokens';
import type { CredentialKeys } from './connectionStringKeywords';
import type { DuplicateKeyRule } from './repeatedKeywords';

export type CredentialField = 'password' | 'login' | 'database';

const WORDS: Readonly<Record<CredentialField, string>> = {
  password: 'password|passwd|pwd',
  login: 'user[ _-]?name|user[ _-]?id|user|login',
  database: 'database[ _-]?name|database|db[ _-]?name|db',
};

function wholeValuePattern(words: string): RegExp {
  const inner = `(?:your[ _-]?)?(?:${words})(?:[ _-]?here)?`;
  const bare = `your[ _-]?(?:${words})(?:[ _-]?here)?`;
  return new RegExp(`^\\s*(?:\\{\\s*${inner}\\s*\\}|<\\s*${inner}\\s*>|\\[\\s*${inner}\\s*\\]|${bare})\\s*$`, 'iu');
}

const PATTERNS: Readonly<Record<CredentialField, RegExp>> = {
  password: wholeValuePattern(WORDS.password),
  login: wholeValuePattern(WORDS.login),
  database: wholeValuePattern(WORDS.database),
};

/** Whether `written` — a value exactly as the string wrote it — is entirely a placeholder for `field`. */
export function isPlaceholder(field: CredentialField, written: string | undefined): boolean {
  return typeof written === 'string' && PATTERNS[field].test(written);
}

/** A pair's value is a placeholder as read, or — an ODBC / JDBC SQL Server `{…}` value — as written, braces and all. */
const isPlaceholderPair = (field: CredentialField, pair: KeyValuePair): boolean =>
  isPlaceholder(field, pair.value) || (pair.braced === true && isPlaceholder(field, `{${pair.value}}`));

/**
 * Which credential a key carries in one format — `password`, `login`, `database` — or, for a secret the form has no
 * field for (an SSL key password, a keystore secret …), that key itself (it is judged with the password pattern).
 */
export type CredentialOf = (key: string) => CredentialField | `secret:${string}` | undefined;

/** `CredentialOf` for one format's keys (matched case-insensitively). */
export function credentialsIn(keys: CredentialKeys): CredentialOf {
  return (key) => {
    const name = key.trim().toLowerCase();
    if (keys.password.includes(name)) return 'password';
    if (keys.login.includes(name)) return 'login';
    if (keys.database.includes(name)) return 'database';
    return keys.secrets.has(name) ? `secret:${name}` : undefined;
  };
}

/**
 * The pairs without the credentials a placeholder leaves unset: for each credential, when its EFFECTIVE value (the pair
 * for it the format's driver uses — the last, or with ODBC the first, a synonym included: `repeatedKeywords.ts`) is a
 * whole placeholder, every pair for it goes; an overridden placeholder goes as well. The format's parser still saw those
 * keys (its validity is checked on all pairs, before this).
 */
export function withoutPlaceholders(pairs: readonly KeyValuePair[], credentialOf: CredentialOf, rule: DuplicateKeyRule = 'last'): KeyValuePair[] {
  const effective = new Map<string, KeyValuePair>();
  for (const pair of pairs) {
    const credential = credentialOf(pair.key);
    if (credential && (rule === 'last' || !effective.has(credential))) effective.set(credential, pair);
  }
  const fieldOf = (credential: string): CredentialField => (credential.startsWith('secret:') ? 'password' : (credential as CredentialField));
  const unset = new Set<string>();
  for (const [credential, pair] of effective) if (isPlaceholderPair(fieldOf(credential), pair)) unset.add(credential);
  // An overridden placeholder has no effect either: it goes too (its braces refuse nothing).
  return pairs.filter((pair) => {
    const credential = credentialOf(pair.key);
    return !credential || (!unset.has(credential) && !isPlaceholderPair(fieldOf(credential), pair));
  });
}

const VALUE_FIELDS: ReadonlyArray<[CredentialField, 'password' | 'login' | 'database', string]> = [
  ['password', 'password', 'password'],
  ['login', 'login', 'user'],
  ['database', 'database', 'database'],
];

/**
 * The same rule for a parsed result whose values are exactly as written (URLs, JDBC URLs, EZConnect, Kusto): a password,
 * user name or database that is entirely a placeholder is dropped with its "filled" label.
 */
export function withoutPlaceholderValues<T extends { values: Record<string, unknown>; matchedFields: string[] }>(match: T): T {
  const unset = VALUE_FIELDS.filter(([field, key]) => isPlaceholder(field, match.values[key] as string | undefined));
  if (unset.length === 0) return match;
  const values = { ...match.values };
  for (const [, key] of unset) delete values[key];
  const labels = new Set(unset.map(([, , label]) => label));
  return { ...match, values, matchedFields: match.matchedFields.filter((label) => !labels.has(label)) };
}
